import { endOfDay, startOfDay, sub } from "date-fns";
import { useMemo } from "react";
import combineStatusData from "../utils/combineStatusData";
import { groupByDeviceId } from "../utils/groupByDeviceId";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { usePresetContext } from "@/components/PresetProvider";
import { useOptionalViewContext } from "@/components/ViewProvider";
import AddInIds from "@/constants/AddInIds";
import { DEFAULT_DIAGNOSTICS_DIAGNOSTICMONITOR } from "@/constants/constants";
import { VEHICLE_STATUS } from "@/constants/groupConfig";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useLatestHistoryByDevice } from "@/hooks/addins/useDeviceHistory";
import { useAddressesByDeviceStatusInfo } from "@/hooks/addresses/useAddresses";
import { useChargeEventsByDeviceIds } from "@/hooks/chargeEvent/useChargeEventsByDeviceIds";
import { useDevicesBySearchWithNumDevices } from "@/hooks/devices/useDevicesBySearch";
import { useDiagnosticsByIds } from "@/hooks/diagnostic/useDiagnosticsByIds";
import { useExceptionEventsByRuleIds } from "@/hooks/exceptionEvent/useExceptionEventsByRuleIds";
import { useLatestFaultData } from "@/hooks/faultData/useLatestFaultData";
import usePlausabilityCheckFaults from "@/hooks/faultData/usePlausabilityCheckFaults";
import { useGroupsByParent } from "@/hooks/groups/useGroupsByParent";
import { useMaintenanceWorkOrder } from "@/hooks/maintenance/useMaintenanceWorkOrder";
import { useMaintenanceWorkOrderJob } from "@/hooks/maintenance/useMaintenanceWorkOrderJob";
import getDiagnosticIdsFromPreset from "@/hooks/presets/getDiagnosticsFromPreset";
import { useRulesByName } from "@/hooks/rules/useRulesByName";
import usePossibleChargingInterruptsByDeviceIds from "@/hooks/statusData/usePossibleChargingInterruptsByDeviceIds";
import { useStatusData } from "@/hooks/statusData/useStatusData";
import { useStatusInfosByIds } from "@/hooks/statusInfos/useStatusInfosByIds";
import { useZonesByIds } from "@/hooks/zone/useZonesByIds";
import ParkingAddInData from "@/models/ParkingAddInData";
import { RemainingRangeAddInData } from "@/models/RemainingRangeAddInData";
import VehicleStatusAddInData, { VehicleStatus } from "@/models/VehicleStatusAddInData";
import { WarningType } from "@/models/Warning";
import { DiagnosticAddinData } from "@/pages/diagnostic-edit/models/DiagnosticAddinData";

export const useDiagnosticMonitorData = () => {
  const { showArchivedVehicles, deviceCountLimit, chargeRateOnlyWhenCharging, pageType } =
    useDataGridSettings();
  const { selectedPreset } = usePresetContext();
  const viewCtx = useOptionalViewContext();

  const { data: deviceHookData = { devices: [], total: 0 }, refetch: refetchDevice } =
    useDevicesBySearchWithNumDevices("", showArchivedVehicles, deviceCountLimit);
  const { devices, total } = deviceHookData;
  const deviceIds = devices?.map(device => device.id) ?? [];

  const selectionDiagnosticIds = viewCtx?.selectedView
    ? viewCtx.selectedView.columns.map(c => c.diagnosticId)
    : getDiagnosticIdsFromPreset(selectedPreset);

  const { data: statusInfos = {}, refetch: refetchStatusData } = useStatusInfosByIds(
    deviceIds,
    DEFAULT_DIAGNOSTICS_DIAGNOSTICMONITOR.concat(selectionDiagnosticIds),
  );

  const { data: odometerAdjustmentData } = useStatusData(
    ["DiagnosticOdometerAdjustmentId"],
    deviceIds,
  );

  const odometerAdjustmentByDevice = odometerAdjustmentData?.["DiagnosticOdometerAdjustmentId"] ?? {};

  const { data: addresses = {} } = useAddressesByDeviceStatusInfo(statusInfos, undefined, true);

  const { data: parkingData = [] } = useAddinData<ParkingAddInData>(AddInIds.parking);
  const { data: remainingRangeData = [] } = useAddinData<RemainingRangeAddInData>(
    AddInIds.remainingRange,
  );

  const zoneIds = Object.values(addresses).reduce((acc: string[], addr) => {
    addr.zones?.forEach(zone => {
      const zoneId = zone?.id ?? "-1";
      if (zoneId !== "-1" && !acc.includes(zoneId)) {
        acc.push(zoneId); // Ensure unique zone IDs are added
      }
    });
    return acc;
  }, []);

  const { data: zones = {} } = useZonesByIds(zoneIds);

  const { data: faultData = [] } = useLatestFaultData(
    deviceIds,
    startOfDay(sub(new Date(), { days: 2 })),
    endOfDay(new Date()),
  );

  const { data: diagnosticData = {} } = useDiagnosticsByIds(
    faultData?.map(fault => fault.diagnostic.id),
    pageType === "statusMonitor",
  );

  const faults = usePlausabilityCheckFaults({ faultData, statusInfos, diagnosticData });

  const { data: descriptions = [] } = useAddinData<DiagnosticAddinData>(
    AddInIds.diagnosticData,
    pageType === "statusMonitor",
  );

  // Predictive Maintenance Events description text is needed
  const { data: pmRuleIdsData = [] } = useRulesByName(["[PM]%"]);
  const pmRuleIds = useMemo(() => pmRuleIdsData?.[0]?.map(r => r.id), [pmRuleIdsData]);
  const { data: pm1RuleIdsData = [] } = useRulesByName(["[PM1]%"]);
  const pm1RuleIds = useMemo(() => pm1RuleIdsData?.[0]?.map(r => r.id), [pm1RuleIdsData]);
  const { data: pmEventData = {} } = useExceptionEventsByRuleIds(pmRuleIds);

  const { data: pm1EventData = {} } = useExceptionEventsByRuleIds(pm1RuleIds);
  const { data: adRuleIdsData = [] } = useRulesByName(["[AD]%"]);

  const adRuleIds = useMemo(() => adRuleIdsData?.[0]?.map(r => r.id), [adRuleIdsData]);
  const { data: adEventData = {} } = useExceptionEventsByRuleIds(adRuleIds);

  const { data: specialRuleIds } = useRulesByName([
    "[TT] - Gelb",
    "[TT] - Rot",
    "[SB] - Anschnallzeichen",
  ]);

  const yellowTellTaleRuleId = specialRuleIds?.[0]?.[0]?.id ?? "";
  const redTellTaleRuleId = specialRuleIds?.[1]?.[0]?.id ?? "";
  const sbRuleId = specialRuleIds?.[2]?.[0]?.id ?? "";

  const electricDevices = Object.values(statusInfos)
    .filter(si =>
      // @ts-ignore
      si.statusData.some(
        // @ts-ignore
        sd => sd.diagnostic.id === "DiagnosticElectricVehicleChargingStateId" && sd.data !== -1,
      ),
    )
    .map(d => d.device.id);

  const notChargingDevices = Object.values(statusInfos)
    .filter(si =>
      // @ts-ignore
      si.statusData.some(
        // @ts-ignore
        sd => sd.diagnostic.id === "DiagnosticElectricVehicleChargingStateId" && sd.data === 0,
      ),
    )
    .map(d => d.device.id);

  const possibleChargeInterruptData = usePossibleChargingInterruptsByDeviceIds(notChargingDevices);
  const { data: chargeEvents = {} } = useChargeEventsByDeviceIds(electricDevices);

  const { data: vehicleStatus = [] } = useAddinData<VehicleStatusAddInData>(AddInIds.vehicleStatus);
  const { data: groups } = useGroupsByParent(VEHICLE_STATUS);
  const vehicleStatusGroups: VehicleStatus[] = useMemo(
    () =>
      vehicleStatus?.map(status => {
        return {
          deviceId: status.deviceId,
          group: groups.find(g => g.id === status.groupId),
          userId: status?.userId,
          dateTime: status?.dateTime,
        };
      }),
    [groups, vehicleStatus],
  );

  const { data: maintenanceWorkOrderJobs = [] } = useMaintenanceWorkOrderJob();
  const { data: maintenanceWorkOrders = [] } = useMaintenanceWorkOrder();
  const jobsByDevice = groupByDeviceId(maintenanceWorkOrderJobs);
  const workOdersByDevice = groupByDeviceId(maintenanceWorkOrders);
  const latestHistoryByDevice = useLatestHistoryByDevice();
  const data = useMemo(
    () =>
      combineStatusData(
        devices,
        addresses,
        zones,
        faults,
        descriptions,
        diagnosticData,
        statusInfos,
        pmRuleIds,
        pm1RuleIds,
        adRuleIds,
        redTellTaleRuleId,
        yellowTellTaleRuleId,
        sbRuleId,
        chargeEvents,
        pmEventData,
        pm1EventData,
        adEventData,
        chargeRateOnlyWhenCharging,
        vehicleStatusGroups,
        jobsByDevice,
        workOdersByDevice,
        latestHistoryByDevice,
        parkingData,
        possibleChargeInterruptData,
        remainingRangeData,
        odometerAdjustmentByDevice,
      ),
    [
      devices,
      addresses,
      zones,
      faults,
      descriptions,
      diagnosticData,
      statusInfos,
      pmRuleIds,
      pm1RuleIds,
      adRuleIds,
      redTellTaleRuleId,
      yellowTellTaleRuleId,
      chargeEvents,
      pmEventData,
      pm1EventData,
      adEventData,
      chargeRateOnlyWhenCharging,
      possibleChargeInterruptData,
      vehicleStatusGroups,
      jobsByDevice,
      workOdersByDevice,
      latestHistoryByDevice,
      sbRuleId,
      parkingData,
      remainingRangeData,
      odometerAdjustmentByDevice,
    ],
  );

  const drivingDevices = data.filter(device => device.isDriving).length ?? 0;
  const chargingDevices =
    data.filter(device => device.isCharging === 1 || device.isCharging === 2)?.length ?? 0;
  const chargeInterruptDevices = data.filter(device => device.chargingInterrupt)?.length ?? 0;
  const withPMDevices =
    data.filter(
      device =>
        device.warnings.filter(
          warning => warning.type === WarningType.PM || warning.type === WarningType.PM1,
        ).length > 0,
    )?.length ?? 0;
  const withStatusDevices = data.filter(device => device.vehicleStatus)?.length ?? 0;

  return {
    data,
    total,
    refetchDevice,
    refetchStatusData,
    drivingDevices,
    chargingDevices,
    chargeInterruptDevices,
    withPMDevices,
    withStatusDevices,
    notChargingDevices: notChargingDevices?.length ?? 0,
  };
};
